import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@repo/ui/lib/utils';
import { Badge } from './badge';

type StatusTone = 'neutral' | 'positive' | 'warning' | 'error';

interface StatusBadgeProps extends Omit<React.ComponentProps<typeof Badge>, 'asChild' | 'variant'> {
  /** Semantic meaning, independent from the metric or workflow using it. */
  tone?: StatusTone;
  icon?: LucideIcon;
}

const badgeVariantByTone = {
  neutral: 'neutral',
  positive: 'success',
  warning: 'warning',
  error: 'error'
} as const;

/**
 * Canonical compact status pill. It deliberately exposes semantic tones
 * instead of the underlying Badge recipes so product screens can share one
 * institutional status language.
 */
function StatusBadge({
  tone = 'neutral',
  icon: Icon,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <Badge
      data-slot="status-badge"
      data-tone={tone}
      variant={badgeVariantByTone[tone]}
      className={cn('text-rowa-label-sm rounded-rowa-full h-6 px-2.5 py-0', className)}
      {...props}
    >
      {Icon && <Icon aria-hidden className="size-3.5" />}
      {children}
    </Badge>
  );
}

export { StatusBadge };
export type { StatusBadgeProps, StatusTone };
